import { useState, Fragment } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import EditLoftLie from './EditLoftLie';
import { useEditLoftLie } from './useEditLoftLie';
import Tile from '@/common/Tile';
import PopoverMenu from '@/common/PopoverMenu';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { Action } from '@/constants';
import { TourBagDetail, TourBagIronDetail, TourBagMstrPayload } from '@/api/models';
import { isMobileResolution } from '@/utils';
import { sortClubsByLoft } from './sortClubsByLoft';
import GreenCheckmarkIcon from '@/assets/icons/green-checkmark.svg?react';
import clubStyles from '../Club.module.scss';
import styles from './LoftLie.module.scss';
import OfflineModal from '@/common/OfflineModal';

const isIronOrUtilityClub = (clubType: string): boolean => ['iron', 'ironSet', 'utility', 'utilitySet'].includes(clubType);

export interface ILoftDropdownOption {
  loftDigit: string | undefined;
  loftFraction: string | undefined;
  joinTableDigitId?: number;
  sourceTableDigitId?: number;
  joinTableFractionId?: number;
  sourceTableFractionId?: number;
}

export interface ILieDropdownOption {
  lieDigit: string | undefined;
  lieFraction: string | undefined;
  joinTableDigitId?: number;
  sourceTableDigitId?: number;
  joinTableFractionId?: number;
  sourceTableFractionId?: number;
}

function LoftLie() {
  const { tourBag, updateTourBag, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const { ironLoftOptions, ironLieOptions, wedgeLoftOptions, wedgeLieOptions } = useEditLoftLie();
  const [showSpinner, setShowSpinner] = useState(false);
  const [editedRowIndex, setEditedRowIndex] = useState<number | null>(null);
  const [editedRowIndexCached, setEditedRowIndexCached] = useState<number | null>(null); //for the offline modal
  const [editedClub, setEditedClub] = useState<(TourBagIronDetail & { type: string }) | (TourBagDetail & { type: string }) | null>(null);
  const [editedLoft, setEditedLoft] = useState<ILoftDropdownOption | null>(null);
  const [editedLie, setEditedLie] = useState<ILieDropdownOption | null>(null);
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);

  const ironBag = tourBag?.find((bag) => bag.headTypeDescription === 'Iron');
  const utilityBag = tourBag?.find((bag) => bag.headTypeDescription === 'Utility');
  const wedgeBag = tourBag?.find((bag) => bag.headTypeDescription === 'Wedge');
  const ironClubs = (ironBag?.tourBagIronDetail || []).sort(sortClubsByLoft).sort((a, b) => a.ironClubName.localeCompare(b.ironClubName));
  const ironSetClubs = [...(ironBag?.tourBagIronSets ?? []).flatMap((set) => set.tourBagIronDetails)].sort((a, b) => a.ironClubName.localeCompare(b.ironClubName));
  const utilityClubs = (utilityBag?.tourBagIronDetail || []).sort(sortClubsByLoft).sort((a, b) => a.ironClubName.localeCompare(b.ironClubName));
  const utilitySetClubs = [...(utilityBag?.tourBagIronSets ?? []).flatMap((set) => set.tourBagIronDetails)].sort((a, b) => a.ironClubName.localeCompare(b.ironClubName));
  const wedgeClubs = (wedgeBag?.tourBagDetail || []).sort(sortClubsByLoft).sort((a, b) => a.headModelName.localeCompare(b.headModelName));
  const allClubs = [
    ...utilityClubs.filter((club) => club.isActive).map((club) => ({ type: 'utility' as const, ...club })),
    ...utilitySetClubs.filter((club) => club.isActive).map((club) => ({ type: 'utilitySet' as const, ...club })),
    ...ironClubs.filter((club) => club.isActive).map((club) => ({ type: 'iron' as const, ...club })),
    ...ironSetClubs.filter((club) => club.isActive).map((club) => ({ type: 'ironSet' as const, ...club })),
    ...wedgeClubs.filter((club) => club.isActive).map((club) => ({ type: 'wedge' as const, ...club })),
  ];

  const buildLoftLiePayload = (club: TourBagIronDetail | TourBagDetail) => ({
    // If lie was not edited fall back to current club value
    loftDigit: editedLoft?.loftDigit?.toString() || club.loftDigit,
    loftDigitId: editedLoft?.joinTableDigitId || club.loftDigitId,
    loftDigitId1: editedLoft?.sourceTableDigitId || club.loftDigitId1,
    loftFraction: editedLoft?.loftFraction || club.loftFraction,
    loftFractionId: editedLoft?.joinTableFractionId || club.loftFractionId,
    loftFractionId1: editedLoft?.sourceTableFractionId || club.loftFractionId1,
    lieDigit: editedLie?.lieDigit?.toString() || club.lieDigit,
    lieDigitId: editedLie?.joinTableDigitId || club.lieDigitId,
    lieDigitId1: editedLie?.sourceTableDigitId || club.lieDigitId1,
    lieFraction: editedLie?.lieFraction || club.lieFraction,
    lieFractionId: editedLie?.joinTableFractionId || club.lieFractionId,
    lieFractionId1: editedLie?.sourceTableFractionId || club.lieFractionId1,
  });

  const handleSave = async () => {
    if (editedRowIndex === null) throw new Error('Cannot save TourBag: no edition in progress.');

    if (isOnline) {
      setShowSpinner(true);
    }
    let updatedBag: TourBagMstrPayload | undefined;
    // if the index is less than the iron length the edition is on an iron club, otherwise its a wedge
    if (editedClub?.type === 'iron') {
      if (!ironBag) throw new Error('Cannot save TourBag: no iron tour bags available.');
      const updatedIrons = ironClubs.map((club) => {
        // cast as iron since we know the type
        if (club.tourBagIronDetailId === (editedClub as TourBagIronDetail).tourBagIronDetailId) {
          return {
            ...club,
            ...buildLoftLiePayload(club),
          };
        }
        return club;
      });
      updatedBag = { ...ironBag, tourBagIronDetail: updatedIrons };
    }

    if (editedClub?.type === 'ironSet') {
      if (!ironBag) throw new Error('Cannot save TourBag: no iron set tour bags available.');
      const updatedSets = ironBag?.tourBagIronSets.map((set) => {
        // Find the set of the edited iron and apply the loft & lie changes
        if (set.tourBagIronSetId === (editedClub as TourBagIronDetail).tourBagIronSetId) {
          const updatedironSetClubs = set.tourBagIronDetails.map((club) => {
            if (club.tourBagIronDetailId === (editedClub as TourBagIronDetail).tourBagIronDetailId) {
              return {
                ...club,
                ...buildLoftLiePayload(club),
              };
            }
            return club;
          });
          return { ...set, tourBagIronDetails: updatedironSetClubs };
        }
        return set;
      });
      updatedBag = { ...ironBag, tourBagIronSets: updatedSets };
    }

    if (editedClub?.type === 'utility') {
      if (!utilityBag) throw new Error('Cannot save TourBag: no utility tour bags available.');
      const updatedUtilities = utilityClubs.map((club) => {
        if (club.tourBagIronDetailId === (editedClub as TourBagIronDetail).tourBagIronDetailId) {
          return {
            ...club,
            ...buildLoftLiePayload(club),
          };
        }
        return club;
      });
      updatedBag = { ...utilityBag, tourBagIronDetail: updatedUtilities };
    }

    if (editedClub?.type === 'utilitySet') {
      if (!utilityBag) throw new Error('Cannot save TourBag: no utility set tour bags available.');
      const updatedSets = utilityBag?.tourBagIronSets.map((set) => {
        if (set.tourBagIronSetId === (editedClub as TourBagIronDetail).tourBagIronSetId) {
          const updatedUtilitySetClubs = set.tourBagIronDetails.map((club) => {
            if (club.tourBagIronDetailId === (editedClub as TourBagIronDetail).tourBagIronDetailId) {
              return {
                ...club,
                ...buildLoftLiePayload(club),
              };
            }
            return club;
          });
          return { ...set, tourBagIronDetails: updatedUtilitySetClubs };
        }
        return set;
      });
      updatedBag = { ...utilityBag, tourBagIronSets: updatedSets };
    }

    if (editedClub?.type === 'wedge') {
      if (!wedgeBag) throw new Error('Cannot save TourBag: no iron tour bags available.');
      const updatedWedges = wedgeClubs.map((club) => {
        if (club.tourBagDetailId === (editedClub as TourBagDetail).tourBagDetailId) {
          return {
            ...club,
            ...buildLoftLiePayload(club),
          };
        }
        return club;
      });
      updatedBag = { ...wedgeBag, tourBagDetail: updatedWedges };
    }

    if (!updatedBag) throw new Error('Cannot save TourBag: no matching bag to update.');

    try {
      if (!editedClub) throw new Error('Cannot save LoftLie: no club selected.');
      const clubId = 'tourBagIronDetailId' in editedClub ? editedClub.tourBagIronDetailId : (editedClub as TourBagDetail).tourBagDetailId;
      // LoftLie is always an update to an existing club property
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, clubId);
      handleCancel(); // exit edition upon success
    } catch (error) {
      console.error('Failed to Edit LoftLie values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (isOnline) {
        setShowSpinner(false);
      } else {
        //they are offline, so show modal
        setShowPostEditOfflineModal(true);
      }
    }
  };

  const handleCancel = () => {
    setEditedRowIndexCached(null);
    setEditedRowIndex(null);
    setEditedLoft(null);
    setEditedLie(null);
  };

  const handleClosePostEditOfflineModal = () => {
    setShowPostEditOfflineModal(false);
  };

  const openOfflineEditModal = (index: number, club: any) => {
    setEditedRowIndexCached(index);
    setEditedClub(club);
    setShowPreEditOfflineModal(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    //get index from cache so the the row is editable
    setEditedRowIndex(editedRowIndexCached);
  };

  const handleEdit = (index: number, club: any) => {
    setEditedRowIndex(index);
    setEditedClub(club);
  };

  return (
    <div className={styles.container} data-testid="tourbag-loft-lie-edit-form-root">
      {!allClubs.length && <div className={clubStyles.noClubs}>No Iron or Wedges in System</div>}
      <div className={styles.list}>
        {allClubs.map((club, index) => {
          const loftValue = `${club.loftDigit}${club.loftFraction}`;
          const lieValue = `${club.lieDigit}${club.lieFraction}`;
          return (
            <Fragment key={'tourBagIronDetailId' in club ? club.tourBagIronDetailId : club.tourBagDetailId}>
              <div className={`${styles.row} ${editedRowIndex === index ? styles.editableRow : ''}`} data-testid="tourbag-loft-lie-row">
                <div className={styles.clubInfo}>
                  <div className={styles.clubName}>{'ironClubName' in club ? club.ironClubName : club.headModelName}</div>
                  <div className={styles.model}>{isIronOrUtilityClub(club.type) ? club.headModelName : club.familyName}</div>
                </div>
                {/* View Loft & Lie */}
                {editedRowIndex !== index && (
                  <>
                    <Tile label="Loft" value={loftValue} className={styles.loftLieTile} />
                    <Tile label="Lie" value={lieValue} className={styles.loftLieTile} />
                  </>
                )}
                {/* Edit Loft & Lie */}
                {editedRowIndex === index && (
                  <EditLoftLie
                    initialLieId={`${club.lieDigit}${club.lieFraction}`}
                    initialLoftId={`${club.loftDigit}${club.loftFraction}`}
                    loftOptions={isIronOrUtilityClub(club.type) ? ironLoftOptions : wedgeLoftOptions}
                    lieOptions={isIronOrUtilityClub(club.type) ? ironLieOptions : wedgeLieOptions}
                    setEditedLoft={setEditedLoft}
                    setEditedLie={setEditedLie}
                  />
                )}
                {/* Group actions buttons for layout */}
                <div className={styles.rowActions}>
                  {editedRowIndex === index && !isMobileResolution && (
                    <div className={styles.editActions}>
                      <button onClick={handleCancel}>Cancel</button>
                      <button onClick={handleSave}>Save</button>
                    </div>
                  )}
                  {editedRowIndex === null && tourPageSettings?.iron === 'full' && isSyncComplete && (
                    <PopoverMenu
                      index={index}
                      onEdit={!isOnline ? () => openOfflineEditModal(index, club) : () => handleEdit(index, club)}
                      showHistory={permissions?.tourBagSettings?.clubHistory ?? false}
                    />
                  )}
                </div>
              </div>
              {editedRowIndex === index && isMobileResolution && (
                <div className={styles.editActions}>
                  <button onClick={handleCancel}>Cancel</button>
                  <button onClick={handleSave}>Save</button>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      {Boolean(allClubs.length) && (
        <div className={styles.allClubsInBag}>
          <GreenCheckmarkIcon /> All clubs are in the bag
        </div>
      )}
      {showSpinner && <SpinnerOverlay />}
      <OfflineModal onCancel={() => setShowPreEditOfflineModal(false)} onClose={showEditFormOfflineModal} isOpen={showPreEditOfflineModal} modalType={'IsOffline'} />
      <OfflineModal onCancel={() => {}} onClose={handleClosePostEditOfflineModal} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </div>
  );
}

export default LoftLie;
